import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { diagnosticsClient, formatTimestamp } from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { Struct } from "@bufbuild/protobuf";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";

export const Route = createFileRoute("/diagnostics/policies")({
	component: DiagnosticsPoliciesPage,
	validateSearch: (search: Record<string, unknown>) => ({
		create: search.create === "1" ? "1" : undefined,
	}),
});

interface PolicyRow {
	id: string;
	name: string;
	version: number;
	status: string;
	captureProfile: string;
	createdAt: unknown;
	updatedAt: unknown;
}

function DiagnosticsPoliciesPage() {
	const { t } = useTranslation("diagnostics");
	useBreadcrumbs([
		{ label: t("title"), href: "/diagnostics" },
		{ label: t("sidebar_policies") },
	]);
	const search = Route.useSearch();
	const { onGridReady, onStateChanged } = useGridLayout("diagnostics-policies");
	const [createOpen, setCreateOpen] = useState(search.create === "1");
	const [detailRow, setDetailRow] = useState<PolicyRow | null>(null);
	const { selectedRows, onSelectionChanged, ActionBar } =
		useGridSelection<PolicyRow>();

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<PolicyRow>({
		fetchRows: async () => {
			const resp = await diagnosticsClient.listRecordingPolicies({});
			const rows = (resp.policies ?? []).map((p) => ({
				...p,
				version: Number(p.version),
			})) as PolicyRow[];
			return {
				rows,
				totalCount: rows.length,
			};
		},
		colToField: {
			captureProfile: "capture_profile",
			createdAt: "created_at",
			updatedAt: "updated_at",
		},
	});

	// ── Column definitions ──
	const columnDefs = useMemo<ColDef<PolicyRow>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "name",
				headerName: t("policies_col_name"),
				flex: 1,
				minWidth: 150,
			},
			{
				field: "version",
				headerName: t("policies_col_version"),
				width: 90,
				filter: "agNumberColumnFilter",
				cellClass: "tabular-nums",
				valueFormatter: (p) => (p.value != null ? `v${p.value}` : ""),
			},
			{
				field: "status",
				headerName: t("policies_col_status"),
				width: 110,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					const color =
						p.value === "active"
							? "hsl(var(--chart-2))"
							: p.value === "suspended"
								? "hsl(var(--destructive))"
								: "hsl(var(--muted-foreground))";
					return (
						<span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, fontSize: 11, lineHeight: "18px", border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`, color }}>
							{t(`enum_status_${p.value}`, { defaultValue: p.value })}
						</span>
					);
				},
			},
			{
				field: "captureProfile",
				headerName: t("policies_col_profile"),
				width: 140,
				cellRenderer: (p: { value: string }) => (
					<span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, fontSize: 11, lineHeight: "18px", border: "1px solid color-mix(in srgb, hsl(var(--muted-foreground)) 20%, transparent)", color: "hsl(var(--muted-foreground))" }}>
						{t(`enum_profile_${p.value}`, { defaultValue: p.value ?? "" })}
					</span>
				),
			},
			{
				field: "createdAt",
				headerName: t("policies_col_created"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => formatTimestamp(p.value),
			},
			{
				field: "updatedAt",
				headerName: t("policies_col_updated"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => formatTimestamp(p.value),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: `${totalRows} ${t("policies_status_count", { defaultValue: "политик" })}`,
				align: "left" as const,
			},
			...(selectedRows.length > 0
				? [
						{
							id: "selection",
							content: t("rows_selected", {
								count: selectedRows.length,
								defaultValue: `${selectedRows.length} выбрано`,
							}),
							align: "right" as const,
						},
					]
				: []),
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("policies_create")}
				</Button>
			</div>
			<div
				className="flex-1 ag-theme-quartz h-full"
				style={{ width: "100%" }}
			>
				<AgGridReact<PolicyRow>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					onRowClicked={(e) => e.data && setDetailRow(e.data)}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("loading", { defaultValue: "Загрузка" })}...</span>`}
				/>
			</div>

			<ActionBar />
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent>
					<CreatePolicyForm
						onSuccess={() => {
							setCreateOpen(false);
							refreshGrid();
							toast.success(t("toast_policy_created"));
						}}
						onCancel={() => setCreateOpen(false)}
					/>
				</DialogContent>
			</Dialog>

			<Dialog
				open={detailRow !== null}
				onOpenChange={(open) => {
					if (!open) setDetailRow(null);
				}}
			>
				<DialogContent>
					{detailRow && (
						<EditPolicyForm
							policy={detailRow}
							onSuccess={() => {
								setDetailRow(null);
								refreshGrid();
								toast.success(t("toast_policy_updated"));
							}}
							onCancel={() => setDetailRow(null)}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}

function EditPolicyForm({
	policy,
	onSuccess,
	onCancel,
}: { policy: PolicyRow; onSuccess: () => void; onCancel: () => void }) {
	const { t } = useTranslation("diagnostics");
	const [name, setName] = useState(policy.name);
	const [captureProfile, setCaptureProfile] = useState(policy.captureProfile);
	const [status, setStatus] = useState(policy.status);
	const [policyJson, setPolicyJson] = useState("");
	const [jsonError, setJsonError] = useState("");

	const mutation = useMutation({
		mutationFn: async () => {
			let policyStruct: Struct | undefined;
			if (policyJson.trim()) {
				const parsed = JSON.parse(policyJson) as Parameters<
					typeof Struct.fromJson
				>[0];
				policyStruct = Struct.fromJson(parsed);
			}
			return diagnosticsClient.upsertRecordingPolicy({
				id: policy.id,
				name,
				captureProfile,
				status,
				policy: policyStruct,
			});
		},
		onSuccess,
		onError: (err: Error) =>
			toast.error(t("toast_error_generic"), { description: err.message }),
	});

	function handleSubmit() {
		if (policyJson.trim()) {
			try {
				JSON.parse(policyJson);
				setJsonError("");
			} catch {
				setJsonError(t("policies_invalid_json"));
				return;
			}
		}
		mutation.mutate();
	}

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("policies_detail_title")}</DialogTitle>
				<DialogDescription>
					{t("policies_detail_description")}
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label>{t("policies_name")}</Label>
					<Input value={name} onChange={(e) => setName(e.target.value)} />
				</div>
				<div className="space-y-2">
					<Label>{t("policies_capture_profile")}</Label>
					<Select value={captureProfile} onValueChange={setCaptureProfile}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="baseline">
								{t("enum_profile_baseline")}
							</SelectItem>
							<SelectItem value="high_fidelity">
								{t("enum_profile_high_fidelity")}
							</SelectItem>
							<SelectItem value="training_candidate">
								{t("enum_profile_training_candidate")}
							</SelectItem>
							<SelectItem value="forensic">
								{t("enum_profile_forensic")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="space-y-2">
					<Label>{t("policies_status_field")}</Label>
					<Select value={status} onValueChange={setStatus}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="draft">{t("enum_status_draft")}</SelectItem>
							<SelectItem value="active">{t("enum_status_active")}</SelectItem>
							<SelectItem value="suspended">
								{t("enum_status_suspended")}
							</SelectItem>
							<SelectItem value="archived">
								{t("enum_status_archived")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="space-y-2">
					<Label>{t("policies_json_label")}</Label>
					<Textarea
						rows={6}
						placeholder={t("policies_json_placeholder")}
						value={policyJson}
						onChange={(e) => {
							setPolicyJson(e.target.value);
							if (jsonError) setJsonError("");
						}}
					/>
					{jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={onCancel}>
					{t("cancel")}
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={mutation.isPending || !name.trim()}
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("policies_saving")}
						</>
					) : (
						t("policies_save")
					)}
				</Button>
			</DialogFooter>
		</>
	);
}

function CreatePolicyForm({
	onSuccess,
	onCancel,
}: { onSuccess: () => void; onCancel: () => void }) {
	const { t } = useTranslation("diagnostics");
	const [name, setName] = useState("");
	const [captureProfile, setCaptureProfile] = useState("baseline");
	const [status, setStatus] = useState("draft");
	const [policyJson, setPolicyJson] = useState("");
	const [jsonError, setJsonError] = useState("");

	const mutation = useMutation({
		mutationFn: async () => {
			let policy: Struct | undefined;
			if (policyJson.trim()) {
				const parsed = JSON.parse(policyJson) as Parameters<
					typeof Struct.fromJson
				>[0];
				policy = Struct.fromJson(parsed);
			}

			return diagnosticsClient.upsertRecordingPolicy({
				name,
				captureProfile,
				status,
				policy,
			});
		},
		onSuccess,
		onError: (err: Error) =>
			toast.error(t("toast_error_generic"), { description: err.message }),
	});

	function handleSubmit() {
		if (policyJson.trim()) {
			try {
				JSON.parse(policyJson);
				setJsonError("");
			} catch {
				setJsonError(t("policies_invalid_json"));
				return;
			}
		}

		mutation.mutate();
	}

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("policies_create_title")}</DialogTitle>
				<DialogDescription>
					{t("policies_create_description")}
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label>{t("policies_name")}</Label>
					<Input
						value={name}
						onChange={(event) => setName(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("policies_capture_profile")}</Label>
					<Select value={captureProfile} onValueChange={setCaptureProfile}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="baseline">
								{t("enum_profile_baseline")}
							</SelectItem>
							<SelectItem value="high_fidelity">
								{t("enum_profile_high_fidelity")}
							</SelectItem>
							<SelectItem value="training_candidate">
								{t("enum_profile_training_candidate")}
							</SelectItem>
							<SelectItem value="forensic">
								{t("enum_profile_forensic")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="space-y-2">
					<Label>{t("policies_status_field")}</Label>
					<Select value={status} onValueChange={setStatus}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="draft">{t("enum_status_draft")}</SelectItem>
							<SelectItem value="active">{t("enum_status_active")}</SelectItem>
							<SelectItem value="suspended">
								{t("enum_status_suspended")}
							</SelectItem>
							<SelectItem value="archived">
								{t("enum_status_archived")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="space-y-2">
					<Label>{t("policies_json_label")}</Label>
					<Textarea
						rows={8}
						placeholder={t("policies_json_placeholder")}
						value={policyJson}
						onChange={(event) => {
							setPolicyJson(event.target.value);
							if (jsonError) setJsonError("");
						}}
					/>
					{jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={onCancel}>
					{t("cancel")}
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={mutation.isPending || !name.trim()}
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("policies_creating")}
						</>
					) : (
						t("policies_create")
					)}
				</Button>
			</DialogFooter>
		</>
	);
}
